import { useEffect, useState } from "react";
import { useUserAccounts } from "@trader-dashboard/shared";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { useOrders } from '@/context/OrdersContext';
import { formatCurrency } from '@/utils/formatCurrency';

interface PairStats {
  symbol: string;
  totalOrders: number;
  totalProfit: number;
  avgProfit: number;
}

interface MonthStats {
  month: string;
  totalOrders: number;
  totalProfit: number;
}

interface SummaryReportData {
  totalOrders: number;
  totalProfit: number;
  byPair: PairStats[];
  byMonth: MonthStats[];
}

export const SummaryReportPage = (): React.ReactNode => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [reportData, setReportData] = useState<SummaryReportData | undefined>(undefined);

  const { selectedAccountId } = useUserAccounts();
  const { getSummaryReport } = useOrders();

  useEffect(() => {
    const loadReport = async () => {
      if (!selectedAccountId) return;

      setLoading(true);
      setError(undefined);

      try {
        const data = await getSummaryReport(selectedAccountId);
        setReportData(data);
      } catch (err: any) {
        console.error('Error loading summary report:', err);
        setError(err.message || 'Failed to load report');
      } finally {
        setLoading(false);
      }
    };

    loadReport();
  }, [selectedAccountId, getSummaryReport]);

  if (loading) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="text-center">Loading summary report...</div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-8">
          <div className="text-center text-red-600">Error: {error}</div>
        </CardContent>
      </Card>
    );
  }

  if (!reportData) {
    return null;
  }

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl">Summary Report</CardTitle>
          <CardDescription>
            Overall trading statistics across all time
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border p-4">
              <div className="text-sm font-medium text-muted-foreground">Total Orders</div>
              <div className="text-3xl font-bold">{reportData.totalOrders}</div>
            </div>
            <div className="rounded-lg border p-4">
              <div className="text-sm font-medium text-muted-foreground">Total Profit</div>
              <div className={`text-3xl font-bold ${reportData.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(reportData.totalProfit)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Statistics by Trading Pair</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Symbol</TableHead>
                <TableHead className="text-right">Total Orders</TableHead>
                <TableHead className="text-right">Total Profit</TableHead>
                <TableHead className="text-right">Avg Profit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reportData.byPair.map((pair) => (
                <TableRow key={pair.symbol}>
                  <TableCell className="font-semibold">{pair.symbol}</TableCell>
                  <TableCell className="text-right">{pair.totalOrders}</TableCell>
                  <TableCell className="text-right">
                    <span className={pair.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {formatCurrency(pair.totalProfit)}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={pair.avgProfit >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {formatCurrency(pair.avgProfit)}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Statistics by Month</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Month</TableHead>
                <TableHead className="text-right">Total Orders</TableHead>
                <TableHead className="text-right">Total Profit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {reportData.byMonth.map((month) => (
                <TableRow key={month.month}>
                  <TableCell className="font-semibold">{month.month}</TableCell>
                  <TableCell className="text-right">{month.totalOrders}</TableCell>
                  <TableCell className="text-right">
                    <span className={month.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}>
                      {formatCurrency(month.totalProfit)}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
